// oxlint-disable no-inline-comments

// Color names from https://www.color-name.com/hex

const primaryColors = [
    "#e6194b", // Venetian Red
    "#3cb44b", // American Green
    "#4363d8", // Royal Blue
    "#f58231", // Cadmium Orange
    "#9a6324", // Golden Brown
    "#f032e6", // Razzle Dazzle Rose
    "#808080", // Gray
    "#ffe119", // Yellow Sun
    "#800000", // Maroon
    "#006400", // Dark Green
    "#000075", // Navy Blue
    "#911eb4", // Violet
    "#000000", // Black
];

const secondaryColors = [
    "#46f0f0", // Turquoise
    "#fabebe", // Spanish Pink
    "#008080", // Teal
    "#e6beff", // Brilliant Lavender
    "#aaffc3", // Magic Mint
    "#808000", // Olive
    "#ffd8b1", // Light Orange
    "#bcf60c", // Bitter Lime
    "#fffac8", // Lemon Chiffon
];

interface ColorState {
    color: string;
    allocated: Set<string>;
    active: Set<string>;
}

class Colors {
    private readonly allocatedKeys: Map<string, ColorState>;
    private readonly colorStates: Map<string, ColorState>;
    private nextIndex: number;

    public constructor(private readonly colors: string[]) {
        this.allocatedKeys = new Map();
        this.colorStates = new Map();
        this.nextIndex = 0;

        for (const color of colors) {
            this.colorStates.set(color, {
                color,
                allocated: new Set(),
                active: new Set(),
            });
        }
    }

    public size(): number {
        return this.colors.length;
    }

    public getAllocated(key: string): string | undefined {
        return this.allocatedKeys.get(key)?.color;
    }

    public getActive(key: string): string | undefined {
        const state = this.allocatedKeys.get(key);
        if (state?.active.has(key)) {
            return state.color;
        }
        return undefined;
    }

    public getNextUnallocated(key: string): string | undefined {
        if (this.nextIndex >= this.colors.length) {
            return undefined;
        }
        const color = this.colors[this.nextIndex++];
        this.allocate(key, color);
        return color;
    }

    public stealFirstUnused(
        key: string,
        activeKeys: ReadonlySet<string>,
    ): string | undefined {
        const state = this.findUnused(activeKeys);

        if (state == null) {
            return undefined;
        }

        // Remove usage of old keys
        const oldKeys = Array.from(state.allocated);
        for (const oldKey of oldKeys) {
            this.deallocate(oldKey);
        }

        // Reuse color for new key
        this.allocate(key, state.color);

        return state.color;
    }

    public updateActive(activeKeys: ReadonlySet<string>): void {
        for (const [key, state] of this.allocatedKeys) {
            if (activeKeys.has(key)) {
                state.active.add(key);
            } else {
                state.active.delete(key);
            }
        }
    }

    public allocateIndex(key: string, index: number): string {
        if (index < 0 || index >= this.colors.length) {
            throw new Error("Index out of bounds");
        }
        const color = this.colors[index];
        this.allocate(key, color);
        return color;
    }

    public deallocate(key: string): void {
        const state = this.allocatedKeys.get(key);

        if (state != null) {
            this.allocatedKeys.delete(key);
            state.allocated.delete(key);
            state.active.delete(key);
        }
    }

    private findUnused(
        activeKeys: ReadonlySet<string>,
    ): ColorState | undefined {
        for (const state of this.colorStates.values()) {
            // If no keys are allocated to this color, we can reuse it.
            if (state.allocated.size === 0) {
                return state;
            }
            // If all allocated keys are not active, we can reuse this color.
            if (!this.hasAllocatedActiveKey(state, activeKeys)) {
                return state;
            }
        }
        return undefined;
    }

    private hasAllocatedActiveKey(
        state: ColorState,
        activeKeys: ReadonlySet<string>,
    ): boolean {
        for (const key of state.allocated) {
            if (activeKeys.has(key)) {
                return true;
            }
        }
        return false;
    }

    private allocate(key: string, color: string): void {
        const state = this.colorStates.get(color);

        if (state == null) {
            throw new Error(`Invalid color: ${color}`);
        }

        state.allocated.add(key);
        state.active.add(key);
        this.allocatedKeys.set(key, state);
    }
}

export class ColorPicker {
    private readonly primaryColors: Colors;
    private readonly secondaryColors: Colors;
    private nextIndex: number;

    public constructor(primaryColors: string[], secondaryColors: string[]) {
        this.primaryColors = new Colors(primaryColors);
        this.secondaryColors = new Colors(secondaryColors);
        this.nextIndex = 0;
    }

    public getMap(keys: string[]): Record<string, string> {
        const res: Record<string, string> = {};
        const missing: string[] = [];
        const activeKeys = new Set(keys);

        for (const key of keys) {
            let color =
                this.primaryColors.getAllocated(key) ??
                this.secondaryColors.getActive(key);

            if (color == null) {
                color =
                    this.primaryColors.getNextUnallocated(key) ??
                    this.primaryColors.stealFirstUnused(key, activeKeys);
                if (color != null) {
                    this.secondaryColors.deallocate(key);
                }
            }

            color ??=
                this.secondaryColors.getAllocated(key) ??
                this.secondaryColors.getNextUnallocated(key) ??
                this.secondaryColors.stealFirstUnused(key, activeKeys);

            if (color != null) {
                res[key] = color;
            } else {
                missing.push(key);
            }
        }

        // Too many keys used at the same time and duplicates are inevitable. Just cycle through all colors.
        if (missing.length > 0) {
            const numColors =
                this.primaryColors.size() + this.secondaryColors.size();
            for (const key of missing) {
                const index = this.nextIndex++ % numColors;
                if (index < this.primaryColors.size()) {
                    res[key] = this.primaryColors.allocateIndex(key, index);
                } else {
                    res[key] = this.secondaryColors.allocateIndex(
                        key,
                        index % this.secondaryColors.size(),
                    );
                }
            }
        }

        this.primaryColors.updateActive(activeKeys);
        this.secondaryColors.updateActive(activeKeys);

        return res;
    }
}

export class DistinctColors extends ColorPicker {
    public constructor() {
        super(primaryColors, secondaryColors);
    }
}
